import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import LetsConnectButton from "./LetsConnectButton";
import HolographicContactModal from "./HolographicContactModal";

const serviceItems = [
  "Branding",
  "Web Design/Development",
  "Content Creation",
  "SEO/AEO",
  "Performance Marketing",
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [showHoloModal, setShowHoloModal] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { label: "Home", href: isHome ? "#home" : "/" },
    { label: "Services", href: isHome ? "#services" : "/#services", hasDropdown: true },
    { label: "Portfolio", href: isHome ? "#works" : "/#works" },
    { label: "Contact Us", href: isHome ? "#contact" : "/#contact" },
  ];

  const handleNavClick = (href) => {
    setIsOpen(false);
    setServicesOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-[1400px]">
        <div className="flex justify-between items-center h-20">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-300"
          >
            <img src="/assets/logo.png" alt="DigiStromer" className="h-10 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => {
              if (link.hasDropdown) {
                return (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      data-testid="nav-link-services"
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="flex items-center gap-1 text-sm font-manrope font-medium tracking-wide text-zinc-400 hover:text-white transition-colors duration-300 relative group"
                    >
                      {link.label}
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`}
                      />
                      <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#ff6b6b] transition-all duration-300 group-hover:w-full" />
                    </button>
                    <AnimatePresence>
                      {servicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-4 w-64 bg-zinc-950/95 backdrop-blur-xl border border-white/10 py-2 shadow-xl"
                          data-testid="services-dropdown"
                        >
                          {serviceItems.map((item, i) => (
                            <a
                              key={item}
                              href={link.href}
                              onClick={() => handleNavClick(link.href)}
                              data-testid={`dropdown-services-${i}`}
                              className="block px-5 py-3 text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors font-light"
                            >
                              {item}
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return link.href.startsWith("/") && !link.href.includes("#") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-sm font-manrope font-medium tracking-wide text-zinc-400 hover:text-white transition-colors duration-300 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#ff6b6b] transition-all duration-300 group-hover:w-full" />
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleNavClick(link.href)}
                  className="text-sm font-manrope font-medium tracking-wide text-zinc-400 hover:text-white transition-colors duration-300 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#ff6b6b] transition-all duration-300 group-hover:w-full" />
                </a>
              );
            })}

            {/* Let's Connect Button */}
            <LetsConnectButton 
              onClick={() => setShowHoloModal(true)} 
              variant="navbar"
            />
          </nav>

          {/* Mobile Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            className="md:hidden text-white w-11 h-11 flex items-center justify-center"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <nav className="container mx-auto px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) =>
                link.hasDropdown ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className="flex items-center gap-2 text-lg font-syne font-semibold text-zinc-300 hover:text-[#ff6b6b] transition-colors"
                    >
                      {link.label}
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-300 ${mobileServicesOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-4 mt-3 flex flex-col gap-3 border-l border-white/10"
                        >
                          {serviceItems.map((item) => (
                            <a
                              key={item}
                              href={link.href}
                              onClick={() => handleNavClick(link.href)}
                              className="text-sm text-zinc-500 hover:text-[#ff6b6b] transition-colors font-light"
                            >
                              {item}
                            </a>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : link.href.startsWith("/") && !link.href.includes("#") ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-lg font-syne font-semibold text-zinc-300 hover:text-[#ff6b6b] transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => handleNavClick(link.href)}
                    className="text-lg font-syne font-semibold text-zinc-300 hover:text-[#ff6b6b] transition-colors"
                  >
                    {link.label}
                  </a>
                )
              )}
              
              {/* Mobile Let's Connect Button */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <LetsConnectButton 
                  onClick={() => {
                    setIsOpen(false);
                    setShowHoloModal(true);
                  }} 
                  variant="navbar"
                  className="w-full"
                />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Holographic Contact Modal */}
      <HolographicContactModal 
        isOpen={showHoloModal} 
        onClose={() => setShowHoloModal(false)} 
      />
    </header>
  );
}
